import { Component, type ReactNode, useEffect } from "react";
import { BrowserRouter } from "react-router-dom";
import { AppRoutes } from "./router";
import { I18nextProvider } from "react-i18next";
import i18n from "./i18n";
import { buildAppUrl, getCanonicalAppOrigin, hasSupabaseAuthPayload } from "./lib/appUrl";
import { PwaInstallPrompt } from "./components/PwaInstallPrompt";
import { NativeBackGesture } from "./components/NativeBackGesture";
import { Capacitor } from "@capacitor/core";

class AppErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: unknown) {
    console.error("[Aero Passport] App surface failed:", error);
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    const isEnglish = i18n.language?.startsWith("en");

    return (
      <div className="min-h-screen bg-[#081321] px-6 text-white">
        <div className="mx-auto flex min-h-screen max-w-xl flex-col items-center justify-center text-center">
          <span className="mb-4 inline-flex h-14 w-14 items-center justify-center rounded-2xl border border-[#C9A84C]/25 bg-[#C9A84C]/10 text-2xl text-[#E8D4A0]">
            <i className="ri-passport-line" />
          </span>
          <h1 className="mb-3 text-2xl font-extrabold">
            {isEnglish ? "We couldn’t open Aero Passport" : "Não conseguimos abrir o Aero Passport"}
          </h1>
          <p className="mb-6 text-sm leading-relaxed text-[#DCE8F4]">
            {isEnglish
              ? "Please refresh the app. If it still does not open, return to the home page and try again."
              : "Atualize o app. Se ainda não abrir, volte para o início e tente novamente."}
          </p>
          <div className="flex w-full flex-col gap-3 sm:flex-row">
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="flex-1 rounded-2xl bg-gradient-to-r from-[#C9A84C] to-[#E8D4A0] px-5 py-3 font-extrabold text-[#0A1628]"
            >
              {isEnglish ? "Refresh app" : "Atualizar app"}
            </button>
            <a
              href="/"
              className="flex-1 rounded-2xl border border-white/15 px-5 py-3 font-bold text-white"
            >
              {isEnglish ? "Back to home" : "Voltar ao início"}
            </a>
          </div>
        </div>
      </div>
    );
  }
}

function AuthCallbackGuard() {
  useEffect(() => {
    if (typeof window === "undefined") return;

    const currentUrl = new URL(window.location.href);
    if (!hasSupabaseAuthPayload(currentUrl)) return;

    const canonicalOrigin = getCanonicalAppOrigin();
    const callbackPrefix = `${canonicalOrigin}/app/auth/callback`;
    const currentPrefix = `${window.location.origin}${currentUrl.pathname}`;

    if (currentPrefix === callbackPrefix) return;

    const redirectUrl = `${buildAppUrl("/app/auth/callback")}${currentUrl.search}${currentUrl.hash}`;
    window.location.replace(redirectUrl);
  }, []);

  return null;
}

function App() {
  useEffect(() => {
    if (Capacitor.getPlatform() === 'ios') document.documentElement.classList.add('capacitor-ios');
    return () => document.documentElement.classList.remove('capacitor-ios');
  }, []);
  return (
    <I18nextProvider i18n={i18n}>
      <BrowserRouter basename={__BASE_PATH__}>
        <AuthCallbackGuard />
        <NativeBackGesture />
        <AppErrorBoundary>
          <AppRoutes />
        </AppErrorBoundary>
        {window.location.pathname !== "/app/discovery-map-frame" && <PwaInstallPrompt />}
      </BrowserRouter>
    </I18nextProvider>
  );
}

export default App;
